
import React, { useState } from 'react';
import { ArrowLeft, Smartphone, MapPin, Sparkles, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import BackButton from '../components/common/BackButton';
import TechLinktree from '../components/linktrees/TechLinktree';
import EcommerceLinktree from '../components/linktrees/EcommerceLinktree';
import ConsultingLinktree from '../components/linktrees/ConsultingLinktree';
import HealthLinktree from '../components/linktrees/HealthLinktree';
import CreativeLinktree from '../components/linktrees/CreativeLinktree';
import FitnessLinktree from '../components/linktrees/FitnessLinktree';
import RestaurantLinktree from '../components/linktrees/RestaurantLinktree';
import LegalLinktree from '../components/linktrees/LegalLinktree';
import EducationLinktree from '../components/linktrees/EducationLinktree';
import RealEstateLinktree from '../components/linktrees/RealEstateLinktree';
import BeautyLinktree from '../components/linktrees/BeautyLinktree';
import SoftwareLinktree from '../components/linktrees/SoftwareLinktree';
import MusicLinktree from '../components/linktrees/MusicLinktree';
import SportsLinktree from '../components/linktrees/SportsLinktree';

const linktreeTypes = [
  { id: 'tech', name: 'Tech/Digital Agency', description: 'Futurista com gradientes vibrantes', category: 'Tecnologia' },
  { id: 'ecommerce', name: 'E-commerce/Retail', description: 'Visual comercial elegante', category: 'Comércio' },
  { id: 'consulting', name: 'Consultoria/Serviços', description: 'Profissional e minimalista', category: 'Negócios' },
  { id: 'health', name: 'Saúde/Wellness', description: 'Clean e confiável', category: 'Saúde' },
  { id: 'creative', name: 'Criativo/Design', description: 'Artístico e vibrante', category: 'Arte' },
  { id: 'fitness', name: 'Fitness/Academia', description: 'Energético e motivacional', category: 'Esporte' },
  { id: 'restaurant', name: 'Restaurante/Food', description: 'Acolhedor e apetitoso', category: 'Gastronomia' },
  { id: 'legal', name: 'Advocacia/Jurídico', description: 'Sério e confiável', category: 'Jurídico' },
  { id: 'education', name: 'Educação/Cursos', description: 'Educativo e inspirador', category: 'Educação' },
  { id: 'realestate', name: 'Imobiliária', description: 'Elegante e profissional', category: 'Imóveis' },
  { id: 'beauty', name: 'Beleza/Estética', description: 'Glamouroso e sofisticado', category: 'Beleza' },
  { id: 'software', name: 'Software/Desenvolvimento', description: 'Moderno e técnico', category: 'Tecnologia' },
  { id: 'music', name: 'Música/Artista', description: 'Vibrante e artístico', category: 'Arte' },
  { id: 'sports', name: 'Esportes/Academia', description: 'Dinâmico e energético', category: 'Esporte' }
];

const Linktrees = () => {
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const renderLinktree = () => {
    switch (selectedType) {
      case 'tech': return <TechLinktree />;
      case 'ecommerce': return <EcommerceLinktree />;
      case 'consulting': return <ConsultingLinktree />;
      case 'health': return <HealthLinktree />;
      case 'creative': return <CreativeLinktree />;
      case 'fitness': return <FitnessLinktree />;
      case 'restaurant': return <RestaurantLinktree />;
      case 'legal': return <LegalLinktree />;
      case 'education': return <EducationLinktree />;
      case 'realestate': return <RealEstateLinktree />;
      case 'beauty': return <BeautyLinktree />;
      case 'software': return <SoftwareLinktree />;
      case 'music': return <MusicLinktree />;
      case 'sports': return <SportsLinktree />;
      default: return null;
    }
  };

  const handleBack = () => {
    setSelectedType(null);
  };

  if (selectedType) {
    return (
      <div className="min-h-screen">
        <BackButton onClick={handleBack} />
        <div className="w-full">
          {renderLinktree()}
        </div>
      </div>
    );
  }

  // Group by category
  const categories = linktreeTypes.reduce((acc, type) => {
    if (!acc[type.category]) {
      acc[type.category] = [];
    }
    acc[type.category].push(type);
    return acc;
  }, {} as Record<string, typeof linktreeTypes>);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 antialiased">
      <Header />
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header with Back to Home Link */}
          <div className="text-center mb-12 animate-fade-in">
            <div className="flex items-center justify-center mb-6">
              <Link
                to="/"
                className="inline-flex items-center px-4 py-2 bg-white text-blue-600 font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 mr-4"
              >
                <Home className="w-4 h-4 mr-2" />
                Voltar para Home
              </Link>
            </div>
            
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Linktrees Profissionais
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              15 designs modernos e profissionais para diferentes tipos de negócios.
              Cada um com QR codes, mapas interativos e animações suaves.
              Clique em qualquer opção para visualizar o Linktree completo.
            </p>
          </div>

          {/* Category Sections */}
          {Object.entries(categories).map(([category, types], categoryIndex) => (
            <div key={category} className="mb-12 animate-fade-in" style={{ animationDelay: `${categoryIndex * 0.2}s` }}>
              <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                {category}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {types.map((type, index) => (
                  <div
                    key={type.id}
                    onClick={() => setSelectedType(type.id)}
                    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 hover:scale-105 animate-scale-in"
                    style={{ animationDelay: `${(categoryIndex * 4 + index) * 0.1}s` }}
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {type.name}
                    </h3>
                    <p className="text-gray-600 mb-4 text-sm">
                      {type.description}
                    </p>
                    <div className="inline-flex items-center text-blue-600 font-medium text-sm hover:text-blue-700 transition-colors">
                      Visualizar Preview
                      <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Features Section */}
          <div className="bg-white rounded-3xl p-8 shadow-xl mt-16 animate-fade-in" style={{ animationDelay: '0.8s' }}>
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
              ✨ Recursos Incluídos
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                  <Smartphone className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">QR Codes Inteligentes</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Acesso instantâneo via smartphone com códigos QR otimizados para facilitar o compartilhamento</p>
              </div>
              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                  <MapPin className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">Mapas Interativos</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Localização integrada com navegação direta para facilitar visitas presenciais dos clientes</p>
              </div>
              <div className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                  <Sparkles className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">Animações Premium</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Transições suaves e efeitos visuais elegantes para uma experiência profissional única</p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-3xl p-8 mt-8 text-white text-center animate-fade-in" style={{ animationDelay: '1s' }}>
            <h3 className="text-2xl font-bold mb-4">Desenvolvido pela Decide Digital</h3>
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              <div className="flex items-center justify-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>Cacoal - Rondônia</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <Smartphone className="w-4 h-4" />
                <span>(69) 99208-3972</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <span>📧</span>
                <span>contato@decidedigital.com.br</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Linktrees;
